/*
 * useObservationSubmit — builds the API payload from form values and
 * dispatches the appropriate create/update call.
 *
 * Issue #707: previously, ObservationSubmit and ObservationEdit each
 * built their own payload inline. The shapes drifted (Edit was missing
 * several constraint fields, etc.) and new fields had to be remembered
 * in both places. This is the single dispatcher both pages route through.
 *
 * Transit-observation recalculation (`exoplanet_transit` metadata) is
 * preserved from ObservationEdit's original logic: when an observation
 * is a transit, recalculate start/end times from the FIXED ingress/egress
 * astronomical events plus user-edited baseline_before/baseline_after.
 *
 * Returns a function `(formValues) => Promise<{ data, isConflict }>`.
 */

import { toExposureCount } from '../../../../utils/exposureCount';
import { apiService } from '../../../../services/apiService';
import { parseRA, parseDec } from '../../../../utils/coordinateFormatting';

// 0 is a real exposure time — "the camera's shortest exposure" — so only a blank/invalid value
// falls back to the 300 s default (|| would have turned 0 into 300).
function exposureSeconds(v) {
  if (v === '' || v == null) return 300;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 300;
}

function mapExposureConfig(e) {
  const ec = {
    filter: e.filter ?? 'L',
    exposure_time: exposureSeconds(e.exposureTime),
    exposure_count: toExposureCount(e.exposureCount),
    binning: typeof e.binning === 'string' ? parseInt(e.binning, 10) || 1 : (e.binning ?? 1),
    gain: e.gain == null || e.gain === '' ? null : Number(e.gain),
    offset: e.offset == null || e.offset === '' ? null : Number(e.offset),
    readout_mode: e.readoutMode ?? null,
  };
  return ec;
}

/**
 * Maps the camel-cased form values back to the server's snake_case
 * payload shape. Inverse of buildObservationFormDefaults.
 *
 * Stage 4: iterates `values.targets` (multi-target shape) instead of
 * synthesising a single target from top-level fields.
 */
export function buildObservationPayload(values, { mode = 'create', loadedObservation = null } = {}) {
  // Convert each target's RA/Dec from whatever the user typed (decimal OR
  // sexagesimal) to canonical decimal degrees. parseRA/parseDec accept both.
  const targets = (values.targets ?? []).map((t, i) => ({
    id: t.id ?? loadedObservation?.targets?.[i]?.id ?? `target_${Date.now()}_${i}`,
    name: t.name ?? '',
    // #1409: carry the spacecraft NAIF id through create + edit submit (only when set).
    ...(t.naif_id ? { naif_id: t.naif_id } : {}),
    ra: parseRA(t.ra),
    dec: parseDec(t.dec),
    epoch: t.epoch ?? 'J2000',
    rotation: Number(t.rotation) || 0,
    priority: t.priority ?? 5,
    sequence_repeat_count: Math.max(1, Number(t.sequenceRepeatCount) || 1),
    exposure_configs: (t.exposureConfigs ?? []).map(mapExposureConfig),
  }));

  // ConstraintsStep registers its fields FLAT (`moonSeparation`, `minAltitude`, ...) while this
  // hook used to read only the nested `constraints.*` copy that useObservationFormDefaults emits.
  // On the edit page the two never met, so every constraint the step rendered was display-only:
  // the controls showed their own hardcoded defaults rather than the stored values, and any change
  // the user made was silently dropped on save. The defaults hook now emits both shapes; read the
  // flat value first and fall back to the nested one for callers that only supply that.
  // (#1157 surfaced this through the moon fields; it applies to all of them.)
  const constraintValue = (flatKey, fallback) => (
    values[flatKey] ?? values.constraints?.[flatKey] ?? fallback
  );

  const constraints = {
    minimum_altitude: constraintValue('minAltitude', 30),
    maximum_airmass: constraintValue('maxAirmass', 2.0),
    // #1157: the separation required AT FULL MOON (the Lorentzian peak), its half-width in days,
    // and the moon-below-horizon override.
    moon_separation: constraintValue('moonSeparation', 60),
    moon_avoidance_width: constraintValue('moonAvoidanceWidth', 5),
    moon_down: constraintValue('moonDown', false),
    maximum_moon_phase: constraintValue('maxMoonPhase', 100),
    maximum_clouds: constraintValue('maxClouds', 30),
    twilight_type: constraintValue('twilightType', 'nautical'),
  };
  if (values.constraints?.startTime) constraints.start_time = values.constraints.startTime;
  if (values.constraints?.endTime) constraints.end_time = values.constraints.endTime;
  if (values.constraints?.baselineBefore != null) constraints.baseline_before = values.constraints.baselineBefore;
  if (values.constraints?.baselineAfter != null) constraints.baseline_after = values.constraints.baselineAfter;

  // Transit recalculation: only for time_based + exoplanet_transit. Uses the
  // stored astronomical ingress/egress as the FIXED anchor — only the user's
  // baseline-window minutes are editable. Original logic preserved verbatim
  // from ObservationEdit; tested separately in this hook's spec.
  if (mode === 'edit'
      && loadedObservation?.observation_type === 'time_based'
      && loadedObservation?.metadata?.observation_type === 'exoplanet_transit') {
    const baselineBefore = values.constraints?.baselineBefore ?? 60;
    const baselineAfter = values.constraints?.baselineAfter ?? 60;
    let ingress, egress;
    if (loadedObservation.metadata.transit_ingress && loadedObservation.metadata.transit_egress) {
      ingress = new Date(loadedObservation.metadata.transit_ingress);
      egress = new Date(loadedObservation.metadata.transit_egress);
    } else if (loadedObservation.metadata.transit_midpoint && loadedObservation.metadata.transit_duration_hours) {
      const mid = new Date(loadedObservation.metadata.transit_midpoint);
      const durMs = loadedObservation.metadata.transit_duration_hours * 60 * 60 * 1000;
      ingress = new Date(mid.getTime() - durMs / 2);
      egress = new Date(mid.getTime() + durMs / 2);
    }
    if (ingress && egress) {
      constraints.start_time = new Date(ingress.getTime() - baselineBefore * 60 * 1000).toISOString();
      constraints.end_time = new Date(egress.getTime() + baselineAfter * 60 * 1000).toISOString();
    }
  }

  const payload = {
    name: values.observationName ?? '',
    description: values.description ?? '',
    priority: values.priority ?? 5,
    observation_type: values.observationType ?? 'standard',
    targets,
    constraints,
    guiding_options: {
      disable_guiding: values.guidingEnabled === false,
      force_guiding: values.requireGuiding === true,
      dither_enabled: values.ditherEnabled !== false,
    },
  };

  if (values.projectId) payload.project_id = values.projectId;
  if (values.assignedTo) payload.assigned_to = values.assignedTo;

  // Global repeat count (how many times to cycle through all targets).
  // On create, only emit when > 1 to keep single-pass payloads clean (the
  // schema default of 1 fills it in anyway). On edit we must ALWAYS emit it:
  // the server PUT only writes fields present in the request body, so omitting
  // it when the user lowers the value back to 1 (turning repeat off) would
  // leave the old count untouched in the DB — the change wouldn't persist.
  const globalRepeat = Math.max(1, Number(values.globalRepeatCount) || 1);
  if (globalRepeat > 1 || mode === 'edit') payload.global_repeat_count = globalRepeat;

  // Stage 2 (#707): time-based / fixed-time / fill-time fields. Server route
  // accepts these in its allowedFields whitelist; we emit them conditionally
  // based on observationType to keep payloads clean for other types.
  if (values.observationType === 'time_based') {
    if (values.minimumObservingTimeMinutes != null) {
      payload.minimum_observing_time_minutes = Number(values.minimumObservingTimeMinutes);
    }
    if (values.desiredObservingTimeMinutes != null) {
      payload.desired_observing_time_minutes = Number(values.desiredObservingTimeMinutes);
    }
    payload.fill_time = values.fillTime === true;
  }
  if (values.observationType === 'fixed_time') {
    if (values.fixedTimeStart) {
      payload.fixed_time_start = values.fixedTimeStart instanceof Date
        ? values.fixedTimeStart.toISOString() : values.fixedTimeStart;
    }
    // #937: ALWAYS emit the key. `null` means "clear it, size the window from the estimate";
    // `undefined` is dropped by the PUT handler and the old padded window survives.
    payload.fixed_time_end = values.fixedTimeEnd
      ? (values.fixedTimeEnd instanceof Date ? values.fixedTimeEnd.toISOString() : values.fixedTimeEnd)
      : null;
    // SUP-2026-0005: emit fill_time explicitly so the toggle persists. Previously
    // omitted, which let the server default/force it — the user's "off" was lost and
    // fixed-time runs filled the whole window with hundreds of exposures.
    // #937: it is now derived from the mode selector rather than a standalone switch.
    payload.fill_time = values.fillTime === true;
    payload.start_tolerance_minutes = Number(values.startToleranceMinutes) || 0;
  }
  // Monitoring / cadence fields. Previously this shared payload builder only
  // handled time_based/fixed_time, so monitoring edits never emitted the
  // cadence_* fields the server whitelists — a cadence/interval/date change in
  // the Edit form was silently dropped. Emit them so monitoring edits persist.
  if (values.observationType === 'monitoring') {
    if (values.cadenceDays != null && values.cadenceDays !== '') {
      payload.cadence_days = parseFloat(values.cadenceDays);
    }
    if (values.cadenceStartDate) {
      payload.cadence_start_date = values.cadenceStartDate instanceof Date
        ? values.cadenceStartDate.toISOString() : values.cadenceStartDate;
    }
    if (values.cadenceEndDate) {
      payload.cadence_end_date = values.cadenceEndDate instanceof Date
        ? values.cadenceEndDate.toISOString() : values.cadenceEndDate;
    }
    payload.cadence_max_observations =
      values.cadenceMaxObservations != null && values.cadenceMaxObservations !== ''
        ? parseInt(values.cadenceMaxObservations, 10)
        : null;
  }

  // Autofocus: empty object = "use observatory defaults" sentinel that
  // matches what ObservationSubmit sends in `useDefaults` mode. Otherwise
  // emit the full trigger_overrides shape matching the schema (Observation.ts:357).
  if (values.useDefaults) {
    payload.autofocus_options = {};
  } else {
    payload.autofocus_options = {
      enabled: values.afEnabled !== false,
      initial_autofocus: values.initialAutofocus !== false,
      trigger_overrides: {
        time_based: {
          enabled: values.afTimeBased !== false,
          interval_minutes: Number(values.afTimeInterval) || 30,
        },
        temperature_based: {
          enabled: values.afTempBased === true,
          threshold_celsius: Number(values.afTempThreshold) || 2.0,
        },
        hfr_based: {
          enabled: values.afHfrBased === true,
          increase_percentage: Number(values.afHfrPercentage) || 5,
        },
        filter_change: { enabled: values.afFilterChange === true },
        after_exposures: {
          enabled: values.afAfterExposures === true,
          exposure_count: Number(values.afAfterExposuresCount) || 10,
        },
      },
    };
  }

  // Metadata: merge user-facing notes/is_fast_mover into existing metadata
  // (server PUT deep-merges nested objects, so other keys round-trip).
  const metadata = {};
  if (values.notes != null && values.notes !== '') metadata.notes = values.notes;
  if (values.isFastMover === true) metadata.is_fast_mover = true;
  if (Object.keys(metadata).length > 0) payload.metadata = metadata;

  return payload;
}

/**
 * Build the repetition_config subdocument from form values. Returns null if
 * the observation isn't repetitive. Mirrors Submit's logic at
 * ObservationSubmit.js:708-737 — the dedicated endpoint expects this shape.
 */
export function buildRepetitionConfig(values) {
  if (values?.isRepetitive !== true) return null;
  const cfg = {
    enabled: true,
    start_date: values.repetitionStartDate instanceof Date
      ? values.repetitionStartDate.toISOString()
      : values.repetitionStartDate,
    interval_days: Number(values.repetitionIntervalDays) || 0,
    interval_hours: Number(values.repetitionIntervalHours) || 0,
    window_hours: Number(values.repetitionWindowHours) || 2,
  };
  if (values.repetitionEndDate) {
    cfg.end_date = values.repetitionEndDate instanceof Date
      ? values.repetitionEndDate.toISOString()
      : values.repetitionEndDate;
  }
  if (values.repetitionMaxExecutions != null && values.repetitionMaxExecutions !== '') {
    cfg.max_executions = Number(values.repetitionMaxExecutions);
  }
  if (values.repetitionSkipIfDelayedHours != null && values.repetitionSkipIfDelayedHours !== '') {
    cfg.skip_if_delayed_hours = Number(values.repetitionSkipIfDelayedHours);
  }
  if (values.repetitionPriorityBoostPerDay != null) {
    cfg.priority_boost_per_day = Number(values.repetitionPriorityBoostPerDay) || 0;
  }
  return cfg;
}

/**
 * Dispatches a create or update. Returns the apiService result.
 * The caller is responsible for try/catch and UI feedback.
 *
 * Edit mode with repetition: two-call flow. The standard PUT updates the
 * observation document but cannot accept repetition_config (its allowedFields
 * intentionally excludes it). The dedicated endpoint
 * PUT /observations/:id/repetition-config recalculates next_execution and
 * series stats, which the standard PUT skips. See #707 plan Stage 3.
 */
export function useObservationSubmit({ mode, observationId, loadedObservation = null } = {}) {
  return async (formValues) => {
    const payload = buildObservationPayload(formValues, { mode, loadedObservation });
    if (mode === 'edit') {
      if (!observationId) throw new Error('observationId required for edit mode');
      const updateResult = await apiService.updateObservation(observationId, payload);
      // Stage 3: dispatch repetition_config to the dedicated endpoint if the
      // observation is (or was) repetitive. Skip when neither current values
      // nor loaded observation indicate repetition — saves a no-op round-trip
      // and avoids re-creating series_stats on plain edits.
      const wasRepetitive = loadedObservation?.repetition_config?.enabled === true;
      const isRepetitive = formValues?.isRepetitive === true;
      if (wasRepetitive || isRepetitive) {
        const repCfg = buildRepetitionConfig(formValues)
          ?? { enabled: false };  // disabling repetition
        await apiService.updateRepetitionConfig(observationId, repCfg);
      }
      return updateResult;
    }
    return apiService.createObservation(payload);
  };
}

export default useObservationSubmit;
